import fetchEndpoint from '@/services/fetchEndpoint';
import buildResultEndpoint from '@/utils/buildResultEndpoint';
import { buildNumPmidMappingConfig } from '@/utils/evidenceTimelineMapping';

export type PolaritySide = 'Positive' | 'Negative';

export type PolarityCounts = {
  positive: number;
  negative: number;
  unlabeled: number;
};

type CountResponse = {
  status?: string;
  paging?: { totalElements?: number };
};

function polarityFilter(side: PolaritySide | 'All') {
  return {
    name: 'association',
    operator: 'eq',
    type: 'select',
    value: [side],
  };
}

export function hasPolarityAssociationScope(
  mappingContent: { mappingItems?: Array<{ filterValue?: string | number }> } | null | undefined,
): boolean {
  const items = mappingContent?.mappingItems ?? [];
  if (items.length === 0) return false;
  return items.every(item => {
    const value = item.filterValue;
    return value != null && String(value).trim() !== '';
  });
}

export function buildPolarityCountEndpoint(params: {
  row: Record<string, unknown>;
  view: string;
  tab: string;
  source: string;
  searchItems?: Array<{ chemID?: string; chemPrefName?: string }>;
  side: PolaritySide | 'All';
}): string | null {
  const mappingContent = buildNumPmidMappingConfig(
    params.row,
    params.view,
    params.tab,
    params.searchItems ?? [],
  );
  if (!hasPolarityAssociationScope(mappingContent)) return null;

  const built = buildResultEndpoint({
    view: params.view || 'GENES',
    source: params.source,
    tab: params.tab,
    searchItems: [],
    isEvidence: true,
    isMapping: true,
    mappingContent,
    pageNum: 0,
    pageSize: 10,
    orderByParam: '',
    order: '',
    defaultFilterValue: [polarityFilter(params.side)],
  });

  return built?.url ?? null;
}

export function readPagingTotal(response: CountResponse | null | undefined): number | null {
  if (!response || response.status !== 'OK') return null;
  const total = Number(response.paging?.totalElements);
  if (!Number.isFinite(total) || total < 0) return null;
  return total;
}

export async function fetchPolarityCounts(params: {
  row: Record<string, unknown>;
  view: string;
  tab: string;
  source: string;
  searchItems?: Array<{ chemID?: string; chemPrefName?: string }>;
  authorization: unknown;
  isAuthenticated: boolean;
  setAuthError: (error: unknown) => void;
}): Promise<PolarityCounts> {
  const sides = ['Positive', 'Negative', 'All'] as const;
  const totals = await Promise.all(
    sides.map(async side => {
      const endpoint = buildPolarityCountEndpoint({ ...params, side });
      if (!endpoint) return null;
      const url = params.isAuthenticated ? endpoint : `${endpoint}&skey=unauth`;
      try {
        const response = (await fetchEndpoint(url, params.authorization, params.setAuthError)) as CountResponse;
        return readPagingTotal(response);
      } catch {
        return null;
      }
    }),
  );

  const [positive, negative, all] = totals;
  if (positive == null || negative == null) throw new Error('Polarity count unavailable');

  return {
    positive,
    negative,
    unlabeled: all == null ? 0 : Math.max(0, all - positive - negative),
  };
}
